// ─── Roles ───────────────────────────────────────────────
export enum UserRole {
  CLIENT = 'CLIENT',
  SURVEYOR = 'SURVEYOR',
  ADMIN = 'ADMIN',
}

// ─── Request States ──────────────────────────────────────
export enum RequestState {
  REQUEST_SUBMITTED = 'REQUEST_SUBMITTED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  APPROVED = 'APPROVED',
  ASSIGNED = 'ASSIGNED',
  NEGOTIATING = 'NEGOTIATING',
  CONFIRMED = 'CONFIRMED',
  CLOSED = 'CLOSED',
  DISPUTED = 'DISPUTED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

// ─── Presence ────────────────────────────────────────────
export type OnlineStatus = 'online' | 'away' | 'offline';

// ─── User ────────────────────────────────────────────────
export interface User {
  id: string;
  phone: string;
  email?: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  avatar_url?: string;
  is_verified: boolean;
  is_active?: boolean;
  is_suspended?: boolean;
  suspension_reason?: string;
  online_status?: OnlineStatus;
  last_seen_at?: string;
  created_at: string;
  updated_at: string;
}

export type CommissionStatus = 'GOOD' | 'OVERDUE' | 'DEFAULTED';

export interface SurveyorProfile extends User {
  full_name?: string;
  service_types: string[];
  base_city: string;
  base_area?: string;
  qualification_type?: 'tertiary' | 'non_tertiary';
  interview_requested?: boolean;
  contact_phone?: string;
  interview_status?: 'pending' | 'scheduled' | 'completed' | 'approved' | 'rejected';
  approved_by_admin?: boolean;
  verification_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  verification_docs: string[];
  rating_average: number;
  total_jobs: number;
  is_available: boolean;
  latitude?: number;
  longitude?: number;
  // ── Anti-cheat / enforcement fields ──────────────────
  is_suspended: boolean;
  suspension_reason?: string;
  suspended_at?: string;
  commission_status: CommissionStatus;
  commission_overdue_amount?: number;
  penalty_points: number;
  reputation_score: number;          // 0-100, starts at 100
}

export interface FlaggedMessage {
  id: string;
  negotiation_id: string;
  sender: User;
  content: string;
  blocked_reason: string;
  created_at: string;
}

export interface CommissionDefault {
  deal_id: string;
  surveyor: SurveyorProfile;
  deal_value: number;
  commission_rate: number;
  expected_commission: number;
  confirmed_at: string;
  days_overdue: number;
  status: CommissionStatus;
}

export interface PenaltyPayload {
  points: number;
  reason: string;
}

export interface SuspendPayload {
  reason: string;
}

// ─── Service Request ─────────────────────────────────────
export interface ServiceRequest {
  id: string;
  ticket_number?: number;
  client: User;
  service_type: string;
  description: string;
  latitude: number;
  longitude: number;
  address: string;
  deadline?: string;
  preferred_date?: string;
  attachments: Attachment[];
  status: RequestState;
  assigned_surveyor?: SurveyorProfile;
  deal_id?: string;          // FK to Deal; may equal id in simplified designs
  deal_value?: number;       // populated once confirmed
  commission_rate?: number;
  expected_commission?: number;
  is_chat_blocked?: boolean;
  pending_proposal?: Proposal | null;
  created_at: string;
  updated_at: string;
}

export interface CreateRequestPayload {
  service_type: string;
  description: string;
  latitude: number;
  longitude: number;
  address: string;
  deadline?: string;
  preferred_date?: string;
}

export interface Attachment {
  id: string;
  file_url: string;
  file_name: string;
  file_type: string;
  created_at: string;
}

// ─── Proposals / Bids ───────────────────────────────────
export interface Proposal {
  id: string;
  survey_request: string;
  request_title?: string;
  surveyor?: string;
  surveyor_email?: string;
  surveyor_profile?: SurveyorProfile | null;
  proposed_price: number;
  message?: string;
  status: 'pending' | 'accepted' | 'rejected';
  deal_id?: string | null;
  chat_room_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateProposalPayload {
  survey_request: string;
  proposed_price: number;
  message?: string;
}

// ─── Deal ────────────────────────────────────────────────
export interface Deal {
  id: string;
  request: ServiceRequest;
  client: User;
  surveyor: SurveyorProfile;
  status: RequestState;
  agreed_price?: number;
  deal_value?: number;
  commission_rate: number;
  expected_commission?: number;
  client_contact?: ContactInfo;
  surveyor_contact?: ContactInfo;
  payment_status?: 'UNPAID' | 'PENDING_APPROVAL' | 'PAID' | 'REJECTED';
  payment_proof?: string;
  latest_payment_id?: string;
  is_chat_blocked?: boolean;
  created_at: string;
  updated_at: string;
  closed_reason?: string;
}

export interface ContactInfo {
  phone?: string;
  email?: string;
}

export interface AcceptDealPayload {
  deal_value: number;
}

export interface RejectDealPayload {
  reason?: string;
}

// ─── Chat ────────────────────────────────────────────────
export interface ChatMessage {
  id: string;
  negotiation_id: string;
  sender: User;
  reply_to?: string;
  reply_to_message?: Pick<ChatMessage, 'id' | 'sender' | 'content' | 'message_type' | 'created_at'> | null;
  content: string;
  message_type: 'TEXT' | 'SYSTEM' | 'BLOCKED' | 'DEAL_PROPOSAL' | 'DEAL';
  proposal_id?: string | null;
  is_blocked: boolean;
  blocked_reason?: string;
  delivered_at?: string;
  read_at?: string;
  read_by?: string[];
  created_at: string;
  attachment?: string;
}

export interface Payment {
  id: string;
  deal: string;
  deal_title?: string;
  surveyor_email?: string;
  amount: number;
  method: 'mobile_money' | 'cash' | 'bank_transfer';
  status: 'pending' | 'pending_approval' | 'paid' | 'rejected' | 'refunded';
  proof_image?: string;
  proof_url?: string;
  submitted_by?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
}

export interface SendMessagePayload {
  content: string;
  negotiation_id: string;
  reply_to?: string;
}

export interface Negotiation {
  id: string;
  chat_room_id?: string;
  request: ServiceRequest;
  client: User;
  surveyor: SurveyorProfile;
  status: RequestState;
  messages: ChatMessage[];
  messages_count?: number;
  blocked_messages_count?: number;
  last_message?: ChatMessage;
  deal_value?: number;
  is_chat_blocked?: boolean;
  blocked_reason?: string;
  is_advertised_job?: boolean;
  job_lead_id?: string;
  advertiser_has_accepted?: boolean;
  taker_has_accepted?: boolean;
  pop_required?: boolean;
  pop_payer_id?: string;
  pop_amount_due?: number;
  created_at: string;
  updated_at: string;
}

// ─── Conversations (Chat Inbox) ──────────────────────────
export type ConversationType = 'REQUEST' | 'CLIENT_SUPPORT' | 'SURVEYOR_SUPPORT';

export interface Conversation {
  id: string;
  ticket_number: number;               // e.g. 1001, 1002 — displayed as #1001
  type: ConversationType;
  subject: string;
  participants: User[];
  request_id?: string;                  // linked request (for REQUEST type)
  deal_id?: string;
  last_message?: ChatMessage;
  unread_count: number;
  is_active: boolean;
  is_blocked: boolean;
  blocked_by?: User;
  blocked_reason?: string;
  created_at: string;
  updated_at: string;
}

// ─── Ratings ─────────────────────────────────────────────
export interface Rating {
  id: string;
  deal_id: string;
  from_user: User;
  to_user: User;
  stars: number;
  comment?: string;
  created_at: string;
}

export interface CreateRatingPayload {
  deal_id: string;
  stars: number;
  comment?: string;
}

// ─── Admin ───────────────────────────────────────────────
export interface AuditLog {
  id: string;
  actor: User;
  action: string;
  entity_type: string;
  entity_id: string;
  metadata: Record<string, any>;
  timestamp: string;
}

export interface CommissionReport {
  deal_id: string;
  client_name: string;
  surveyor_name: string;
  deal_value: number;
  commission_rate: number;
  expected_commission: number;
  realised_commission?: number;
  unrealised_commission?: number;
  commission_status?: 'REALISED' | 'UNREALISED';
  payment_status?: Payment['status'];
  confirmed_at: string;
}

export interface CommissionSummary {
  period: string;
  total_deals: number;
  total_deal_value: number;
  total_expected_commission: number;
  total_realised_commission?: number;
  total_unrealised_commission?: number;
  by_surveyor: {
    surveyor_id: string;
    surveyor_name: string;
    deals: number;
    total_value: number;
    total_commission: number;
    realised_commission?: number;
    unrealised_commission?: number;
  }[];
}

export interface SurveyorMetrics {
  surveyor_id: string;
  surveyor_name: string;
  jobs_assigned: number;
  deals_confirmed: number;
  deals_closed: number;
  average_response_time?: number;
  rating_average: number;
  dispute_rate: number;
}

// ─── API ─────────────────────────────────────────────────
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ApiError {
  detail?: string;
  message?: string;
  errors?: Record<string, string[]>;
}

// ─── WebSocket ───────────────────────────────────────────
export enum WSEventType {
  CHAT_MESSAGE = 'chat_message',
  DEAL_UPDATE = 'deal_update',
  REQUEST_UPDATE = 'request_update',
  TYPING = 'typing',
  MESSAGE_BLOCKED = 'message_blocked',
  MESSAGE_READ = 'message_read',
  PRESENCE = 'presence',
}

export interface WSMessage {
  type: WSEventType;
  payload: any;
}

// ─── Notifications ───────────────────────────────────────
export enum NotificationType {
  NEW_REQUEST = 'new_request',
  SURVEYOR_ASSIGNED = 'surveyor_assigned',
  DEAL_ACCEPTED = 'deal_accepted',
  DEAL_REJECTED = 'deal_rejected',
  NEW_CHAT_MESSAGE = 'new_chat_message',
  ADMIN_ALERT = 'admin_alert',
}

export interface PushNotificationPayload {
  type: NotificationType;
  title: string;
  body: string;
  data: {
    request_id?: string;
    deal_id?: string;
    negotiation_id?: string;
  };
}

// ─── Service Types ───────────────────────────────────────
export const SURVEY_SERVICE_TYPES = [
  // Water & Borehole
  'Borehole Siting Survey',
  'Hydrogeological Survey',
  'Water Quality Assessment',
  'Groundwater Exploration',
  'Borehole Yield Testing',
  'Aquifer Assessment',
  'Water Source Mapping',
  'Geophysical Water Survey',
  'Well Rehabilitation Survey',
  'Water Table Monitoring',
  // Borehole Drilling & Rig Services
  'Borehole Drilling',
  'Borehole Drilling Rig Hiring',
  'Borehole Casing & Lining',
  'Borehole Pump Installation',
  'Borehole Rehabilitation',
  'Drilling Site Preparation',
] as const;

export type SurveyServiceType = (typeof SURVEY_SERVICE_TYPES)[number];

// ─── Service Categories (grouped metadata for UI) ────────
export interface SurveyCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  services: string[];
}

export const SURVEY_CATEGORIES: SurveyCategory[] = [
  {
    id: 'water',
    name: 'Water & Borehole Surveys',
    icon: '💧',
    color: '#06B6D4',
    description:
      'Hydrological and groundwater investigations for water source identification, borehole siting, quality testing, and aquifer characterisation.',
    services: [
      'Borehole Siting Survey',
      'Hydrogeological Survey',
      'Water Quality Assessment',
      'Groundwater Exploration',
      'Borehole Yield Testing',
      'Aquifer Assessment',
      'Water Source Mapping',
      'Geophysical Water Survey',
      'Well Rehabilitation Survey',
      'Water Table Monitoring',
    ],
  },
  {
    id: 'borehole_drilling',
    name: 'Borehole Drilling & Rig Services',
    icon: '🛢️',
    color: '#B45309',
    description:
      'Professional borehole drilling, rig hiring, casing, pump installation, and rehabilitation services for residential, commercial, and agricultural water supply projects across Zimbabwe.',
    services: [
      'Borehole Drilling',
      'Borehole Drilling Rig Hiring',
      'Borehole Casing & Lining',
      'Borehole Pump Installation',
      'Borehole Rehabilitation',
      'Drilling Site Preparation',
    ],
  },
];

// ─── Job Leads (Surveyor Referral Advertising) ───────────
export enum JobLeadStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED         = 'APPROVED',
  REJECTED         = 'REJECTED',
  FULFILLED        = 'FULFILLED',
}

/**
 * A job opportunity scouted by a surveyor in the field.
 * Goes through admin approval before appearing on the platform.
 * On deal closure the platform commission is split between admin
 * and the referring surveyor according to the agreed percentages.
 */
export interface JobLead {
  id: string;
  referrer: SurveyorProfile;        // surveyor who scouted the lead
  service_type: string;
  client_name: string;
  client_phone: string;
  location_address: string;
  latitude?: number;
  longitude?: number;
  description: string;
  estimated_value?: number;         // USD estimate provided by referrer
  platform_commission_rate?: number;
  estimated_platform_commission?: number;
  requested_referrer_share_pct: number;
  requested_referrer_commission?: number;
  status: JobLeadStatus;
  rejection_reason?: string;
  notes?: string;                   // admin notes on approval
  // ── Profit split (set by admin on approval) ─────────
  admin_share_pct: number;          // e.g. 70  → admin keeps 70% of platform commission
  referrer_share_pct: number;       // e.g. 30  → referrer earns 30% of platform commission
  estimated_referrer_commission?: number;
  // ── Settlement (populated after deal closes) ─────────
  linked_request_id?: string;
  linked_deal_id?: string;
  payout_amount?: number;           // actual USD paid to referrer
  created_at: string;
  updated_at: string;
}

export interface CreateJobLeadPayload {
  service_type: string;
  client_name: string;
  client_phone: string;
  location_address: string;
  latitude?: number;
  longitude?: number;
  description: string;
  estimated_value?: number;
  requested_referrer_share_pct: number;
}

export interface ApproveJobLeadPayload {
  admin_share_pct: number;
  referrer_share_pct: number;
  notes?: string;
}
